/**
 * Remastered colour (docs/spec/04 §8): each cave colour becomes a five-shade ramp. Shadows lean
 * toward a deep indigo and highlights toward a warm white, the way pixel artists shift hue across
 * a ramp, so the cave keeps its C64 identity with far more depth. Pure.
 */

import { rgb } from '../palette';

export type RGB = readonly [number, number, number];

/** Five shades, darkest first; index 2 is the C64 colour itself. */
export type Ramp = readonly [RGB, RGB, RGB, RGB, RGB];

export interface RampSet {
  readonly earth: Ramp;
  readonly rock: Ramp;
  readonly bright: Ramp;
  /** Near-black outline, tinted by the earth colour. */
  readonly ink: RGB;
}

const SHADOW: RGB = [20, 14, 42];
const LIGHT: RGB = [255, 246, 222];
const DARK_MIX = [0.64, 0.34];
const LIGHT_MIX = [0.3, 0.58];

export function mix(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t].map((v) => Math.round(v)) as unknown as RGB;
}

export function rampOf(base: RGB): Ramp {
  return [
    mix(base, SHADOW, DARK_MIX[0] ?? 0.6),
    mix(base, SHADOW, DARK_MIX[1] ?? 0.3),
    base,
    mix(base, LIGHT, LIGHT_MIX[0] ?? 0.3),
    mix(base, LIGHT, LIGHT_MIX[1] ?? 0.6),
  ];
}

/** Ramps for a cave's C64 colours c1, c2, c3. */
export function rampSet(colors: readonly [number, number, number]): RampSet {
  const earth = rampOf(rgb(colors[0]));
  return { earth, rock: rampOf(rgb(colors[1])), bright: rampOf(rgb(colors[2])), ink: mix(earth[0], [6, 4, 14], 0.78) };
}

/** Relative luminance 0-1, for tests and contrast checks. */
export function luma(c: RGB): number {
  return (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255;
}
